import classNames from 'classnames';
import { isNumber } from 'lodash';
import { memo, type ReactNode, useEffect, useState } from 'react';

import { Close } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';

export type ToastColor = 'black' | 'red' | 'green';

const TOAST_COLOR_CLASS_NAMES: { [key in ToastColor]: string } = {
  black: 'bg-pigeon-700 text-white',
  green: 'bg-green-4 text-green-12',
  red: 'bg-flamingo-600 text-white',
};

export type ToastItemProps = {
  children: ReactNode;
  color?: ToastColor;
  onDismiss: () => void;

  /** This is a component that can show in front of the message.. */
  prefix?: ReactNode;
  /** This is a component that can show in back of the message.. */
  suffix?: ReactNode;
  timeBeforeAutoDismiss?: number | null;
  /**
   * Controls whether the assistive technology should read immediately ("assertive") or wait until the
   * user is idle ("polite"). Think to yourself: "If this toast wasn't visible, would a user be confused?"
   * If the answer to that question is "yes", the type should probably be "assertive".
   *
   * "assertive" makes the toast have `role="alert"` and "polite" makes the toast have `role="status"`
   *
   * @default 'polite'
   * @example Success notifications can be polite, and server errors from a form submission should be alert.
   * @see https://reach.tech/alert#alert-type
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_status_role
   */
  type?: 'assertive' | 'polite';
  withCloseButton?: boolean;
};

export const ToastItem = memo(
  ({
    children,
    color = 'black',
    onDismiss,
    prefix,
    suffix,
    timeBeforeAutoDismiss,
    type,
    withCloseButton,
  }: ToastItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: dismiss as dependency causes issue with resetting the timeout
    useEffect(() => {
      let timer = 0;

      if (isNumber(timeBeforeAutoDismiss) && !isHovered) {
        timer = window.setTimeout(onDismiss, timeBeforeAutoDismiss);
      }

      return () => clearTimeout(timer);
    }, [isHovered, timeBeforeAutoDismiss]);

    return (
      <div
        className={classNames(
          'relative my-1 flex min-h-[48px] items-center gap-3 overflow-hidden rounded-8 px-4 py-3 shadow-popover',
          TOAST_COLOR_CLASS_NAMES[color],
        )}
        data-testid="TOAST_ITEM"
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        role={type === 'assertive' ? 'alert' : 'status'}
      >
        {prefix ? (
          <div className="shrink-0" data-testid="TOAST_PREFIX">
            {prefix}
          </div>
        ) : null}

        <span className="font-medium text-14" data-testid="TOAST_TEXT">
          {children}
        </span>

        {suffix ? (
          <div className="shrink-0" data-testid="TOAST_PREFIX">
            {suffix}
          </div>
        ) : null}

        {withCloseButton && (
          <IconButton
            appearance="ghost"
            className="shrink-0 text-white/80"
            color="grey"
            icon={Close}
            label="Close toast"
            onClick={onDismiss}
            size="small"
          />
        )}
      </div>
    );
  },
);

ToastItem.displayName = 'ToastItem';
